/**
 * 売上タブ — 受注（accepted）の金額・手数料・手取りを一覧と合計で表示。
 * 旧デモUIの「売上」画面の実データ版。
 */
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Landmark, Check } from "lucide-react";
import { api, type BankAccount } from "@/lib/api";
import { calcFee, calcPayout, withTax } from "@/lib/fee";

export function EarningsPage() {
  const { data: bids, isLoading } = useQuery({ queryKey: ["my-bids"], queryFn: () => api.listMyBids() });
  if (isLoading) return <p className="p-5 text-sm text-ink-soft">読み込み中...</p>;

  const accepted = (bids ?? []).filter((b) => b.status === "accepted");
  const gross = accepted.reduce((s, b) => s + b.amount, 0);        // 税抜合計
  const fees = accepted.reduce((s, b) => s + calcFee(b.amount), 0);
  const net = accepted.reduce((s, b) => s + calcPayout(b.amount), 0); // 手取り＝税込−手数料

  return (
    <div className="px-5 py-5 space-y-4">
      <div className="bg-gradient-to-br from-brand-500 to-brand-700 rounded-2xl p-5 text-white shadow-lg">
        <div className="text-xs opacity-80">受注合計（手取り）</div>
        <div className="text-3xl font-black mt-1 tabular-nums">¥{net.toLocaleString("ja-JP")}</div>
        <div className="text-xs opacity-80 mt-1 tabular-nums">
          受注総額(税抜) ¥{gross.toLocaleString("ja-JP")} ／ 手数料 −¥{fees.toLocaleString("ja-JP")}（手取り＝税込−手数料）
        </div>
      </div>

      {accepted.length === 0 && (
        <p className="text-sm text-ink-soft text-center py-8">まだ受注がありません。案件一覧から応札しましょう。</p>
      )}

      <ul className="space-y-3">
        {accepted.map((b) => {
          const fee = calcFee(b.amount);
          return (
            <li key={b.id} className="bg-white dark:bg-white/5 rounded-xl border border-[var(--color-border)] p-4">
              <div className="flex items-center justify-between">
                <span className="text-xs text-ink-soft dark:text-white/60">案件 {b.case_id.slice(0, 8)}</span>
                <span className="text-xs px-2 py-0.5 bg-brand-50 dark:bg-brand-800/30 text-brand-700 dark:text-brand-200 rounded-full font-bold">受注</span>
              </div>
              <div className="mt-2 grid grid-cols-3 gap-2 text-center tabular-nums">
                <div>
                  <div className="text-[10px] text-ink-soft dark:text-white/50">受注額(税込)</div>
                  <div className="text-sm font-bold">¥{withTax(b.amount).toLocaleString("ja-JP")}</div>
                </div>
                <div>
                  <div className="text-[10px] text-ink-soft dark:text-white/50">手数料</div>
                  <div className="text-sm font-bold text-red-600">−¥{fee.toLocaleString("ja-JP")}</div>
                </div>
                <div>
                  <div className="text-[10px] text-ink-soft dark:text-white/50">手取り</div>
                  <div className="text-sm font-black text-brand-600 dark:text-brand-300">¥{calcPayout(b.amount).toLocaleString("ja-JP")}</div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {/* 振込先口座の登録（元請モデル：支良洲工業から外注費をこの口座へ振込） */}
      <BankAccountCard />
    </div>
  );
}

const EMPTY: BankAccount = { bank: "", branch: "", account: "", account_holder: "", account_type: "普通" };

function BankAccountCard() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["my-bank"], queryFn: () => api.getMyBankAccount() });
  const [form, setForm] = useState<BankAccount>(EMPTY);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (data) setForm({ bank: data.bank ?? "", branch: data.branch ?? "", account: data.account ?? "", account_holder: data.account_holder ?? "", account_type: data.account_type ?? "普通" });
  }, [data]);

  const mut = useMutation({
    mutationFn: () => api.saveMyBankAccount(form),
    onSuccess: () => { setSaved(true); qc.invalidateQueries({ queryKey: ["my-bank"] }); setTimeout(() => setSaved(false), 2500); },
  });

  const set = (k: keyof BankAccount, v: string) => setForm((p) => ({ ...p, [k]: v }));
  const filled = !!(form.bank && form.branch && form.account && form.account_holder);

  return (
    <div className="bg-white dark:bg-white/5 rounded-2xl border border-[var(--color-border)] p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Landmark size={16} className="text-brand-600" />
        <p className="text-sm font-bold">振込先口座（外注費の受取）</p>
      </div>
      <p className="text-xs text-ink-soft dark:text-white/60 leading-relaxed">
        工事代金（手取り）は、シタミー運営（合同会社支良洲工業）から<strong>この口座へお振込</strong>します。正確にご登録ください。
      </p>
      <div className="grid grid-cols-2 gap-2">
        <label className="col-span-1 text-xs">銀行名
          <input value={form.bank ?? ""} onChange={(e) => set("bank", e.target.value)} placeholder="例：清水銀行"
            className="mt-1 w-full border border-[var(--color-border)] rounded-md px-2 py-2 bg-transparent text-sm" />
        </label>
        <label className="col-span-1 text-xs">支店名
          <input value={form.branch ?? ""} onChange={(e) => set("branch", e.target.value)} placeholder="例：富士支店"
            className="mt-1 w-full border border-[var(--color-border)] rounded-md px-2 py-2 bg-transparent text-sm" />
        </label>
        <label className="col-span-1 text-xs">種別
          <select value={form.account_type ?? "普通"} onChange={(e) => set("account_type", e.target.value)}
            className="mt-1 w-full border border-[var(--color-border)] rounded-md px-2 py-2 bg-transparent text-sm">
            <option value="普通">普通</option>
            <option value="当座">当座</option>
          </select>
        </label>
        <label className="col-span-1 text-xs">口座番号
          <input value={form.account ?? ""} onChange={(e) => set("account", e.target.value)} inputMode="numeric" placeholder="例：1234567"
            className="mt-1 w-full border border-[var(--color-border)] rounded-md px-2 py-2 bg-transparent text-sm" />
        </label>
        <label className="col-span-2 text-xs">口座名義（カナ）
          <input value={form.account_holder ?? ""} onChange={(e) => set("account_holder", e.target.value)} placeholder="例：カ）シラスコウギョウ"
            className="mt-1 w-full border border-[var(--color-border)] rounded-md px-2 py-2 bg-transparent text-sm" />
        </label>
      </div>
      {mut.error && <p className="text-xs text-red-600">{(mut.error as Error).message}</p>}
      <button
        onClick={() => mut.mutate()}
        disabled={!filled || mut.isPending}
        className="w-full flex items-center justify-center gap-2 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white text-sm font-bold py-2.5 rounded-xl active:scale-95 transition"
      >
        {saved ? <><Check size={15} /> 保存しました</> : mut.isPending ? "保存中..." : "口座を登録・更新する"}
      </button>
    </div>
  );
}
